import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Tag } from 'lucide-react';
import axios from 'axios';
import { PageSEO } from '../components/PageSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    }
  };

  const filteredNews = filter === 'all' 
    ? news 
    : news.filter(n => n.category === filter);

  const categories = [
    { id: 'all', label: 'Tümü' },
    { id: 'duyuru', label: 'Duyurular' },
    { id: 'etkinlik', label: 'Etkinlikler' },
    { id: 'haber', label: 'Haberler' }
  ];

  return (
    <div data-testid="news-page">
      <PageSEO title="Haberler" description="KOSAD'dan en güncel arıcılık haberleri, etkinlik duyuruları ve sektör gelişmeleri." path="/haberler" />
      {/* Hero */}
      <section className="relative py-32 bg-[#1A3625]">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">Haberler</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl text-white font-semibold mb-6">
              Duyurular ve Haberler
            </h1>
            <p className="text-base text-white/80">
              KOSAD'dan en son gelişmeler, etkinlikler ve duyurular.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white sticky top-20 z-40 border-b border-[#E5DAC8]">
        <div className="container-custom">
          <div className="flex flex-wrap gap-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setFilter(cat.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  filter === cat.id 
                    ? 'bg-[#1A3625] text-white' 
                    : 'bg-[#F9F8F6] text-[#5C5C5C] hover:bg-[#E5DAC8]'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((item, index) => (
              <motion.article
                key={item.id}
                className="card-premium group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`news-item-${item.id}`}
              >
                <div className="aspect-video rounded-2xl overflow-hidden mb-6">
                  <img 
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                
                <div className="flex items-center gap-4 mb-4">
                  <span className="flex items-center gap-1 text-sm text-[#8C7A6B]">
                    <Calendar className="w-4 h-4" />
                    {new Date(item.date).toLocaleDateString('tr-TR', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-[#8C7A6B] capitalize">
                    <Tag className="w-4 h-4" />
                    {item.category}
                  </span>
                </div>
                
                <h2 className="text-lg font-medium text-[#1A3625] mb-3 group-hover:text-[#D4AF37] transition-colors">
                  {item.title}
                </h2>
                <p className="text-[#5C5C5C] mb-4">{item.summary}</p>
                
                <button className="text-[#1A3625] font-medium text-base hover:text-[#D4AF37] transition-colors">
                  Devamını Oku →
                </button>
              </motion.article>
            ))}
          </div>
          
          {filteredNews.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#5C5C5C]">Bu kategoride henüz haber bulunmuyor.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
